import { IHero } from "@src/components/Hero";
import { motion } from "framer-motion";
import Image from "next/image";
import React from "react";
import image from "../../../../public/images/image10.png";
import Icon from "../../Icon";
import Boop from "src/components/Boop";
import Dialog from "src/components/Dialog";
import { useFloater } from "@src/hooks/useFloater";
import { InvitationData } from "src/components/modals/SeatingReservation";

// Extended interface to include Floater props
interface IHeroWithFloater extends IHero {
  accessToken: string | null;
  hasWallet?: boolean;
  getEventStreamersData: any;
  liveSchedules: any;
  access_control: boolean;
  qr_code: string;
  getAsoebiData: any;
  admits: number;
  isInvitedToEvent: boolean;
  invitation?: InvitationData | null;
}

const Hero: React.FC<IHeroWithFloater> = ({
  eventId,
  heroImageData,
  slugData,
  loading,
  eventUuid,
  accessToken,
  getEventStreamersData,
  liveSchedules,
  access_control,
  qr_code,
  getAsoebiData,
  admits,
  isInvitedToEvent,
  invitation,
}) => {
  const [heroFloaterVisible, setHeroFloaterVisible] =
    React.useState<boolean>(true);

  // Use the same hook as Floater for consistent visible/overflow and dropdown
  const {
    visibleItems,
    overflowItems,
    hasOverflow,
    hasItemsToShow,
    isExpanded,
    toggleExpanded,
    trackView,
  } = useFloater({
    variant: "minimalist",
    getEventStreamersData,
    eventId,
    slugData,
    liveSchedules,
    access_control,
    qr_code,
    accessToken,
    getAsoebiData,
    eventUuid,
    admits,
    invitation,
    isInvitedToEvent: isInvitedToEvent ?? false,
  });

  // Handle scroll to show/hide hero floater based on main floater visibility
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const viewportHeight = window.innerHeight;

      // Hide hero floater when main floater should appear (same logic as home component)
      // Main floater appears at 1% of viewport height, so hide hero floater at that point
      setHeroFloaterVisible(scrollPosition <= viewportHeight * 0.01);
    };

    window.addEventListener("scroll", handleScroll);

    // Check initial position
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="w-full h-[100vh] relative overflow-hidden">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.8,
            ease: [0.79, 0.14, 0.15, 0.86],
          },
        }}
        className="absolute top-0 left-0 w-full z-[5] pt-8 px-4 bg-transparent"
      >
        <div className="w-full px-2 md:px-6 flex justify-between items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{
              opacity: 1,
              x: 0,
              transition: {
                delay: 0.2,
                duration: 0.6,
              },
            }}
            className="uppercase text-white font-light font-gill tracking-[0.2rem] mb-2 text-xs md:text-base"
          >
            {loading
              ? "..."
              : !!slugData?.eventBySlug && !!slugData?.eventBySlug?.hash_tag
              ? slugData?.eventBySlug?.hash_tag
              : ""}
          </motion.div>
          {/* <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{
              opacity: 1,
              x: 0,
              transition: {
                delay: 0.2,
                duration: 0.6,
              },
            }}
            className="text-white font-light font-gill tracking-[0.1rem] text-xs md:text-base mb-2"
          >
            LAGOS, NIGERIA
          </motion.div> */}
        </div>
      </motion.header>

      {/* Background Image */}
      <motion.div
        initial={{ scale: 1 }}
        animate={{
          scale: 1.1,
          transition: {
            delay: 0.3,
            duration: 0.8,
            ease: [0.79, 0.14, 0.15, 0.86],
          },
        }}
        className={
          "relative top-0 bottom-0 right-0 left-0 w-full h-full after:absolute after:w-full after:h-full after:bg-template-2-hero"
        }
      >
        <Image
          loading="eager"
          layout="fill"
          src={
            !!heroImageData.getHeroImage ? heroImageData.getHeroImage : image
          }
          alt="gallery-image"
          className="object-cover object-center"
        />
      </motion.div>

      {/* Main Content */}
      <div className="w-full h-full absolute left-0 top-0 z-[4]">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{
            opacity: 1,
            transition: {
              delayChildren: 1,
              staggerChildren: 1.5,
            },
          }}
          viewport={{ once: true }}
          className={"w-full h-full flex flex-col justify-end px-6 relative"}
        >
          <motion.h5
            initial={{ opacity: 0, y: 30 }}
            whileInView={{
              opacity: 1,
              y: 0,
              transition: {
                duration: 1,
                ease: [0.79, 0.14, 0.15, 0.86],
              },
            }}
            className={
              "text-[2rem] md:text-[3.5rem] text-white font-light tracking-wide font-gill mt-8 mb-2 leading-[100%] uppercase"
            }
          >
            {slugData?.eventBySlug?.name}
          </motion.h5>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{
              opacity: 1,
              y: 0,
              transition: {
                duration: 1,
                ease: [0.79, 0.14, 0.15, 0.86],
              },
            }}
            className="font-light font-gill text-[1rem] sm:text-[1.4rem] text-white leading-[100%] mb-[1rem]"
          >
            {loading
              ? "..."
              : !!slugData?.eventBySlug && !!slugData?.eventBySlug?.welcome_msg
              ? slugData?.eventBySlug?.welcome_msg
              : ""}
          </motion.p>

          {/* Hero-specific Floater - same visible/overflow + More dropdown as main Floater */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{
              opacity: 1,
              y: 0,
              transition: {
                delay: 0.2,
                duration: 1,
                ease: [0.79, 0.14, 0.15, 0.86],
              },
            }}
            className="mb-[3rem]"
          >
            <div className="flex flex-row items-start justify-start w-full">
              {heroFloaterVisible && hasItemsToShow ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    transition: {
                      duration: 0.3,
                      ease: [0.79, 0.14, 0.15, 0.86],
                    },
                  }}
                  exit={{
                    opacity: 0,
                    transition: {
                      duration: 0.2,
                    },
                  }}
                  className="flex flex-col bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3 md:px-6 md:py-4 shadow-lg w-fit"
                >
                  <div className="flex flex-row items-center gap-4 md:gap-6 lg:gap-8 flex-wrap">
                    {visibleItems.map((item) => (
                      <Dialog
                        key={item.title}
                        fillScreen={item.fillScreen}
                        trigger={
                          <Boop y={2}>
                            <div className="flex flex-col items-center cursor-pointer group">
                              <div className="h-8 md:h-10 flex items-center justify-center text-black group-hover:text-gray-600 transition-colors duration-200">
                                <Icon iconName={item.icon} />
                              </div>
                              <p className="font-gill font-light text-xs md:text-sm text-black text-center mt-2 capitalize whitespace-nowrap group-hover:text-gray-600 transition-colors duration-200">
                                {item.title}
                              </p>
                            </div>
                          </Boop>
                        }
                        open={item.open}
                        onOpenChange={(open) => {
                          if (open) trackView(item.title);
                          item.setOpen(open);
                        }}
                      >
                        {item.modal}
                      </Dialog>
                    ))}

                    {hasOverflow && overflowItems.length > 0 && (
                      <Boop y={2}>
                        <button
                          type="button"
                          onClick={toggleExpanded}
                          aria-label={
                            isExpanded
                              ? "Show less options"
                              : "Show more options"
                          }
                          className="flex flex-col items-center cursor-pointer group outline-none border-0 bg-transparent p-0"
                        >
                          <div className="h-8 md:h-10 flex items-center justify-center text-black group-hover:text-gray-600 transition-colors duration-200">
                            <Icon
                              iconName={
                                isExpanded ? "chevronUp" : "chevronDown"
                              }
                            />
                          </div>
                          <p className="font-gill font-light text-xs md:text-sm text-black text-center mt-2 capitalize whitespace-nowrap group-hover:text-gray-600 transition-colors duration-200">
                            {isExpanded ? "Less" : "More"}
                          </p>
                        </button>
                      </Boop>
                    )}
                  </div>

                  {hasOverflow && overflowItems.length > 0 && (
                    <div
                      className="grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out w-full"
                      style={{
                        gridTemplateRows: isExpanded ? "1fr" : "0fr",
                      }}
                    >
                      <div className="min-h-0 flex flex-row flex-wrap items-center gap-4 md:gap-6 lg:gap-8 overflow-hidden pt-4">
                        {overflowItems.map((item) => (
                          <Dialog
                            key={item.title}
                            fillScreen={item.fillScreen}
                            trigger={
                              <Boop y={2}>
                                <div className="flex flex-col items-center cursor-pointer group">
                                  <div className="h-8 md:h-10 flex items-center justify-center text-black group-hover:text-gray-600 transition-colors duration-200">
                                    <Icon iconName={item.icon} />
                                  </div>
                                  <p className="font-gill font-light text-xs md:text-sm text-black text-center mt-2 capitalize whitespace-nowrap group-hover:text-gray-600 transition-colors duration-200">
                                    {item.title}
                                  </p>
                                </div>
                              </Boop>
                            }
                            open={item.open}
                            onOpenChange={(open) => {
                              if (open) trackView(item.title);
                              item.setOpen(open);
                            }}
                          >
                            {item.modal}
                          </Dialog>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              ) : (
                // Invisible placeholder to maintain spacing
                <div className="flex flex-row items-center gap-4 md:gap-6 lg:gap-8 px-4 py-3 md:px-6 md:py-4 opacity-0 pointer-events-none">
                  <div className="flex flex-col items-center">
                    <div className="h-8 md:h-10"></div>
                    <p className="font-gill font-light text-xs md:text-sm mt-2 capitalize whitespace-nowrap">
                      Placeholder
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Hero;
